import React, { useCallback, useEffect, useState } from "react";
import { getUccV2, updateUccV2 } from "../../../api/bseApi";
import {
  buildUpdateUccPayload,
  detailFromGetUcc,
  emptyUccUpdateForm,
  isBseSuccess,
  parseBseMessages,
  validateUccUpdateForm,
} from "../../../utils/uccUpdateHelpers";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  zIndex: 1050,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};

const modalStyle = {
  background: "#fff",
  borderRadius: "10px",
  boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
  width: "100%",
  maxWidth: "820px",
  maxHeight: "92vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const bodyStyle = {
  overflow: "auto",
  padding: "0 24px 24px",
  flex: 1,
};

const TABS = [
  { id: "account", label: "Account" },
  { id: "personal", label: "Personal" },
  { id: "address", label: "Address" },
  { id: "bank", label: "Bank" },
  { id: "demat", label: "Demat" },
];

export default function UccUpdateModal({ open, onClose, clientCode, memberId, onUpdated }) {
  const [form, setForm] = useState(emptyUccUpdateForm);
  const [tab, setTab] = useState("account");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadDetail = useCallback(async () => {
    if (!open || !clientCode) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const mid = String(memberId || "63164").trim() || "63164";
      const res = await getUccV2({
        data: {
          member: { member_id: mid },
          investor: { client_code: String(clientCode).trim() },
        },
      });
      const body = res?.data;
      if (!isBseSuccess(body)) {
        throw new Error(parseBseMessages(body) || "Could not load UCC details");
      }
      setForm({ ...emptyUccUpdateForm, ...detailFromGetUcc(body) });
    } catch (err) {
      const apiBody = err?.response?.data;
      setError(parseBseMessages(apiBody) || err?.message || "Failed to load UCC");
      setForm(emptyUccUpdateForm);
    } finally {
      setLoading(false);
    }
  }, [open, clientCode, memberId]);

  useEffect(() => {
    if (open) {
      setTab("account");
      loadDetail();
    } else {
      setForm(emptyUccUpdateForm);
      setError("");
      setSuccess("");
    }
  }, [open, loadDetail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    const validationError = validateUccUpdateForm(form);
    if (validationError) {
      setError(validationError);
      setSaving(false);
      return;
    }

    try {
      const mid = String(memberId || "63164").trim() || "63164";
      const payload = buildUpdateUccPayload(form, mid, clientCode);
      const res = await updateUccV2(payload);
      const body = res?.data;
      if (!isBseSuccess(body)) {
        throw new Error(parseBseMessages(body) || "Update was rejected by BSE");
      }

      const makerId = body?.data?.id;
      setSuccess(
        makerId
          ? `Update submitted (maker id ${makerId}). BSE may require member approval.`
          : "Update submitted successfully."
      );
      if (onUpdated) onUpdated();
    } catch (err) {
      const apiBody = err?.response?.data;
      const hint = apiBody?.data?.hint;
      const base = parseBseMessages(apiBody) || err?.message || "Update failed";
      setError(hint ? `${base} ${hint}` : base);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const disabled = loading || saving;

  const selectField = (name, label, options) => (
    <div className="mb-3" key={name}>
      <label className="form-label small fw-semibold mb-1">{label}</label>
      <select
        className="form-select form-select-sm"
        value={form[name]}
        onChange={(e) => setForm((prev) => ({ ...prev, [name]: e.target.value }))}
        disabled={disabled}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );

  const inputField = (name, label, opts = {}) => (
    <div className="mb-3" key={name}>
      <label className="form-label small fw-semibold mb-1">{label}</label>
      <input
        type={opts.type || "text"}
        className="form-control form-control-sm"
        value={form[name]}
        onChange={(e) => {
          let v = e.target.value;
          if (opts.upper) v = v.toUpperCase();
          if (opts.digitsOnly) v = v.replace(/\D/g, "");
          if (opts.maxLen) v = v.slice(0, opts.maxLen);
          setForm((prev) => ({ ...prev, [name]: v }));
        }}
        required={opts.required !== false}
        disabled={disabled}
        maxLength={opts.maxLen}
      />
    </div>
  );

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid #e2e8f0" }}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h5 className="mb-1">Update UCC</h5>
              <p className="text-muted small mb-0">
                Client code <strong>{clientCode}</strong>
                {form.uccStatus ? (
                  <>
                    {" "}
                    · <strong>{form.uccStatus}</strong>
                  </>
                ) : null}
              </p>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <ul className="nav nav-tabs mt-3" style={{ fontSize: "13px" }}>
            {TABS.map((t) => (
              <li className="nav-item" key={t.id}>
                <button
                  type="button"
                  className={`nav-link ${tab === t.id ? "active" : ""}`}
                  onClick={() => setTab(t.id)}
                  disabled={disabled}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div style={bodyStyle}>
          {loading && <p className="text-muted small pt-2">Loading from BSE…</p>}
          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          {success && <div className="alert alert-success py-2 small">{success}</div>}
          <form onSubmit={handleSubmit} className="pt-2">
            {tab === "account" && (
              <div className="row">
                <div className="col-md-6">
                  {selectField("holding_nature", "Holding nature", [
                    { value: "SI", label: "Single" },
                    { value: "JO", label: "Joint" },
                  ])}
                </div>
                <div className="col-md-6">
                  {selectField("tax_code", "Tax status", [
                    { value: "01", label: "Resident individual" },
                    { value: "02", label: "NRI – NRE" },
                    { value: "03", label: "NRI – NRO" },
                  ])}
                </div>
                <div className="col-md-6">
                  {selectField("rdmp_idcw_pay_mode", "Redemption / IDCW pay mode", [
                    { value: "02", label: "Direct credit" },
                    { value: "01", label: "Cheque" },
                  ])}
                </div>
                <div className="col-md-6">
                  {selectField("comm_mode", "Communication mode", [
                    { value: "E", label: "Email" },
                    { value: "M", label: "Mobile" },
                    { value: "P", label: "Physical" },
                  ])}
                </div>
              </div>
            )}
            {tab === "personal" && (
              <>
                <div className="row">
                  <div className="col-md-4">{inputField("first_name", "First name")}</div>
                  <div className="col-md-4">
                    {inputField("middle_name", "Middle name", { required: false })}
                  </div>
                  <div className="col-md-4">{inputField("last_name", "Last name")}</div>
                </div>
                <div className="row">
                  <div className="col-md-4">{inputField("dob", "Date of birth", { type: "date" })}</div>
                  <div className="col-md-4">
                    {selectField("gender", "Gender", [
                      { value: "M", label: "Male" },
                      { value: "F", label: "Female" },
                      { value: "O", label: "Other" },
                    ])}
                  </div>
                  <div className="col-md-4">{inputField("pan", "PAN", { upper: true, maxLen: 10 })}</div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    {selectField("occ_code", "Occupation", [
                      { value: "02", label: "Business" },
                      { value: "01", label: "Service" },
                      { value: "03", label: "Professional" },
                      { value: "04", label: "Others" },
                    ])}
                  </div>
                  <div className="col-md-4">
                    {selectField("auth_mode", "Auth mode", [
                      { value: "M", label: "Mobile" },
                      { value: "E", label: "Email" },
                    ])}
                  </div>
                  <div className="col-md-4">
                    {selectField("kyc_type", "KYC type", [
                      { value: "K", label: "KRA verified" },
                      { value: "B", label: "Biometric" },
                    ])}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">{inputField("email", "Email", { type: "email" })}</div>
                  <div className="col-md-3">
                    {inputField("phone", "Mobile", { digitsOnly: true, maxLen: 10 })}
                  </div>
                  <div className="col-md-3">
                    {inputField("country_code", "Country code", {
                      required: false,
                      digitsOnly: true,
                      maxLen: 3,
                    })}
                  </div>
                </div>
              </>
            )}
            {tab === "address" && (
              <>
                {inputField("addr1", "Address line 1")}
                {inputField("addr2", "Address line 2", { required: false })}
                {inputField("addr3", "Address line 3", { required: false })}
                <div className="row">
                  <div className="col-md-4">{inputField("city", "City")}</div>
                  <div className="col-md-4">{inputField("state", "State / UT")}</div>
                  <div className="col-md-4">
                    {inputField("postalcode", "PIN code", { digitsOnly: true, maxLen: 6 })}
                  </div>
                </div>
                {inputField("country", "Country", { required: false })}
              </>
            )}
            {tab === "bank" && (
              <>
                <p className="small text-muted">
                  Changing bank details deletes the previous account and adds the new one (BSE
                  requirement).
                </p>
                <div className="row">
                  <div className="col-12">
                    {inputField("ifsc_code", "IFSC", { upper: true, maxLen: 11 })}
                  </div>
                  <div className="col-12">{inputField("bank_acc_num", "Account number")}</div>
                  <div className="col-md-6">
                    {selectField("bank_acc_type", "Account type", [
                      { value: "SB", label: "Savings" },
                      { value: "CB", label: "Current" },
                      { value: "NE", label: "NRE" },
                      { value: "NO", label: "NRO" },
                    ])}
                  </div>
                </div>
              </>
            )}
            {tab === "demat" && (
              <>
                <p className="small text-muted">
                  Demat changes use add/delete when DP or client ID changes.
                </p>
                <div className="row">
                  <div className="col-md-6">
                    {selectField("depository_code", "Depository", [
                      { value: "CDSL", label: "CDSL" },
                      { value: "NSDL", label: "NSDL" },
                    ])}
                  </div>
                  <div className="col-md-6">{inputField("dp_id", "DP ID")}</div>
                  <div className="col-md-6">{inputField("client_id", "Demat client ID")}</div>
                </div>
              </>
            )}
            <div
              className="d-flex justify-content-end gap-2 pt-3 mt-2"
              style={{ borderTop: "1px solid #e2e8f0" }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={disabled}>
                {saving ? "Saving…" : "Save all changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
